import { useCallback, useEffect, useState } from 'react';
import { Popover, Input } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';

import DatePicker from '../DatePicker/DatePickerWithoutFormik';

import { SPOTIFY_TOKEN } from '../../constants/storage';
import { useLoginContext } from '../../context';
import { usePlaylistContext } from '../../context/PlaylistContext';
import {
  AddOrEditEndDate,
  AddOrEditNotes,
  addItemToStorage,
  editSongEndDateAndNotes,
  editSongToPlaylist,
  getPlaylistItemsSpotify,
  getRefreshedToken,
} from '../../utils';
import { resolveOriginalDateFromRefreshRecord } from '../../utils/playlistRefreshRecord';
import { ADD_TO_PLAYLIST_DATE_QUICK_OFFSETS } from '../../utils/addToPlaylistForm';

import { AddToPlayListModalStyle } from './AddToPlayListModal.style';

import Spinner from '../Spinner/Spinner';

interface AddToPlayListModalProps {
  isModalVisible?: boolean;
  setIsModalVisible?: (_: boolean) => void;
  editType: String;
  setEditType: any;
  ViewDetailData?: any;
  editId?: any;
  preRelease?: boolean;
  loadingText?: string;
  hideUriInput?: boolean;
  currentPlaylistData?: any;
  PlaylistRefreshRecord?: any;
  setIndexes?: any;
  loadingTextHead?: any;
  setViewDetailData?: any;
  playlistIdRef?: any;
  currentPage?: any;
  currentSongs?: any[];
  onEndDateSuccess?: (songUri: string, newEndDate: string, songId: string) => void;
  onNotesSuccess?: (songUri: string, newNotes: string, songId: string) => void;
  onMoveSongSuccess?: (
    songUri: string,
    oldPlaylistId: string,
    newPlaylistId: string | string[] | null,
    newPosition: number | null
  ) => void;
}

const EditAddToPlayListModal = (props: AddToPlayListModalProps) => {
  const {
    isModalVisible,
    setIsModalVisible,
    editType,
    ViewDetailData,
    loadingText,
    currentPlaylistData,
    PlaylistRefreshRecord,
    setIndexes,
    loadingTextHead,
    setViewDetailData,
    playlistIdRef,
    currentPage,
    currentSongs,
    onEndDateSuccess,
    onNotesSuccess,
    onMoveSongSuccess,
  } = props;
  const { playlistId } = useParams();

  // State variables
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [searchPlaylistText, setSearchPlaylistText] = useState('');
  const [EndDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectPlayListOpen, setSelectPlayListOpen] = useState(false);
  const [totalPlaylist, setTotalPlaylist] = useState([]);
  const [totalPlaylistNumber, setTotalPlaylistNumber] = useState(0);
  const [selectedPlayList, setSelectedPlayList] = useState([]);
  const [data, setData] = useState({
    playlistId: [],
    startTime: '',
    endTime: '',
    position: 0,
    status: 'past',
  });

  // Context variables
  const { state } = usePlaylistContext();
  const { state: loginState, dispatch: loginDispatch } = useLoginContext();
  const viewDetailTrackUri =
    ViewDetailData?.track?.uri || ViewDetailData?.item?.uri || ViewDetailData?.songUri;
  const resolvedOriginalDate = resolveOriginalDateFromRefreshRecord(
    PlaylistRefreshRecord,
    viewDetailTrackUri,
    ViewDetailData?.added_at
  );

  const handleOk = () => {
    setIsModalVisible(false);
    if (setViewDetailData) {
      setViewDetailData('');
    }
    setSearchPlaylistText('');
    setSelectPlayListOpen(false);
  };

  // Helper function to get current position from updated songs state
  const getCurrentPosition = useCallback(
    (songUri: string) => {
      if (!currentSongs) return null;

      const songIndex = currentSongs.findIndex(
        song => song.item?.uri === songUri || song.rowNumber?.e?.item?.uri === songUri
      );

      return songIndex >= 0 ? songIndex : null;
    },
    [currentSongs]
  );

  const handleCancel = (e: any) => {
    e.stopPropagation();
    setIsModalVisible(false);
    setData({
      playlistId: [],
      startTime: '',
      endTime: '',
      position: 0,
      status: 'past',
    });
    setSelectedPlayList([playlistIdRef?.current]);
    if (setViewDetailData) {
      setViewDetailData('');
    }
    setNotes('');
    setCurrentDate(new Date());
    setEndDate('');
    setSelectPlayListOpen(false);
    setSearchPlaylistText('');
    setSelectPlayListOpen(false);
  };

  useEffect(() => {
    if (isModalVisible) {
      axios(`https://api.spotify.com/v1/me/playlists`, {
        method: 'GET',
        headers: { Authorization: 'Bearer ' + loginState.token },
      })
        .then(genreResponse => {
          setTotalPlaylist(genreResponse?.data.items);
        })
        .catch(async () => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalVisible]);

  const addToPlaylistLoop = async data => {
    setLoading(true);

    try {
      // Get Auth0 user ID from localStorage (not Spotify ID from loginState)
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const auth0UserId = userInfo?.userId || '';

      // Process all playlists
      const promises = data.playlistId.map(async a => {
        const res = await getPlaylistItemsSpotify(a, loginState.token);
        let adjustedPosition = data.position;

        if (res.total === 0) {
          adjustedPosition = 0;
        } else if (data.position > res.total) {
          adjustedPosition = playlistId === a ? res.total - 1 + 1 : res.total + 1;
        }

        // Call the API for each playlist
        return editSongToPlaylist(
          playlistIdRef?.current,
          a,
          data.songUri,
          loginState.token,
          loginState.refreshToken,
          auth0UserId,  // Use Auth0 user ID, not Spotify ID
          adjustedPosition,
          data.start_time || data.startTime, // Try both field names
          (data.end_time || data.endTime) === 'null' ? '' : (data.end_time || data.endTime),
          'past',
          data.playId,
          data.indexes,
          data.notes,
          data.currentposition || setIndexes
        );
      });

      // Wait for all API calls to complete
      await Promise.all(promises);

      toast.success('Song moved successfully');
      setLoading(false);
      setIsModalVisible(false);

      // Check if current playlist is still included in the target playlists
      const currentPlaylistStillIncluded = data.playlistId.includes(playlistIdRef?.current);

      // Update state without reload if we have the callback
      if (onMoveSongSuccess) {
        if (currentPlaylistStillIncluded) {
          // Current playlist is included, update position
          onMoveSongSuccess(data.songUri, playlistIdRef?.current, data.playlistId, data.position);
        } else {
          // Song was removed from current playlist
          onMoveSongSuccess(data.songUri, playlistIdRef?.current, null, null);
        }
      }
    } catch (error) {
      toast.error('Error while moving song');
      setLoading(false);
    }
  };

  const handleNotesSubmit = async (values: any) => {
    if (values.delete) {
      setLoading2(true);
    } else {
      setLoading(true);
    }

    try {
      const finalNotes = values.delete ? '' : notes;

      if (props.preRelease) {
        await AddOrEditNotes(finalNotes, props.editId);
      } else {
        // Get current position from updated state to handle optimistic updates
        const currentPosition = getCurrentPosition(values.songUri);
        const positionToUse =
          currentPosition !== null
            ? currentPosition + 1
            : values.position
            ? values.position + 1
            : setIndexes + values.index + 1;

        // Validate and sanitize endTime to prevent invalid date errors
        // Use _rawEndTime if available (contains actual string), otherwise use endTime
        let rawEndTime = values._rawEndTime || values.endTime;
        let safeEndTime = rawEndTime;

        if (rawEndTime && rawEndTime !== 'null' && typeof rawEndTime === 'string') {
          try {
            const dateTest = new Date(rawEndTime);
            if (isNaN(dateTest.getTime())) {
              // Invalid date, use null instead
              safeEndTime = null;
              console.warn('Invalid endTime detected, using null:', rawEndTime);
            }
          } catch (error) {
            // Date parsing failed, use null
            safeEndTime = null;
            console.warn('Date parsing failed for endTime, using null:', rawEndTime);
          }
        } else if (rawEndTime && typeof rawEndTime !== 'string') {
          // Handle React elements or other non-string types
          safeEndTime = null;
          console.warn('Non-string endTime detected, using null:', typeof rawEndTime);
        }

        await editSongEndDateAndNotes(
          playlistId ? playlistId : values.playlist,
          playlistId ? playlistId : values.playlist,
          values.songUri,
          loginState.token,
          loginState.refreshToken,
          loginState.userId,
          positionToUse,
          values.startTime,
          safeEndTime,
          'past',
          values.playId,
          values.indexes,
          finalNotes,
          currentPosition !== null
            ? currentPosition
            : values.currentposition || setIndexes + values.index
        );
      }

      // SUCCESS: Update parent state instead of reload
      onNotesSuccess?.(values.songUri, finalNotes, values._id);
      toast.success(values.delete ? 'Note deleted successfully' : 'Note updated successfully');
      setIsModalVisible(false);
    } catch (error) {
      console.log('Error:', error);
      toast.error('Error while updating note');
    } finally {
      if (values.delete) {
        setLoading2(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleEndDateSubmit = (values: any) => {
    if (values.delete) {
      setLoading2(true);
    } else {
      setLoading(true);
    }
    if (props.preRelease) {
      // Validate and sanitize EndDate for preRelease path
      // For end date editing, use the EndDate state variable (from DatePicker)
      let safeEndDatePreRelease = values.delete ? 'null' : EndDate;

      if (!values.delete && EndDate && typeof EndDate === 'string') {
        try {
          const dateTest = new Date(EndDate);
          if (isNaN(dateTest.getTime())) {
            safeEndDatePreRelease = 'null';
            console.warn('Invalid EndDate detected in preRelease, using null:', EndDate);
          }
        } catch (error) {
          safeEndDatePreRelease = 'null';
          console.warn('Date parsing failed for EndDate in preRelease, using null:', EndDate);
        }
      } else if (!values.delete && EndDate && typeof EndDate !== 'string') {
        safeEndDatePreRelease = 'null';
        console.warn('Non-string EndDate detected in preRelease, using null:', typeof EndDate);
      }

      AddOrEditEndDate(safeEndDatePreRelease, props.editId)
        .then(response => {
          toast.success(values.delete ? 'End time has been deleted' : 'End time added to playlist');
          if (values.delete) {
            setLoading2(false);
          } else {
            setLoading(false);
          }
          setIsModalVisible(false);
          // Update parent state instead of reload
          onEndDateSuccess?.(values.songUri, safeEndDatePreRelease, values._id);
        })
        .catch(() => {
          toast.error('Error while adding End time!');
          if (values.delete) {
            setLoading2(false);
          } else {
            setLoading(false);
          }
        });
    } else {
      // Validate and sanitize EndDate to prevent invalid date errors
      // For end date editing, use the EndDate state variable (from DatePicker)
      let safeEndDate = values.delete ? 'null' : EndDate;

      if (!values.delete && EndDate && typeof EndDate === 'string') {
        try {
          const dateTest = new Date(EndDate);
          if (isNaN(dateTest.getTime())) {
            // Invalid date, use null instead
            safeEndDate = 'null';
            console.warn('Invalid EndDate detected, using null:', EndDate);
          }
        } catch (error) {
          // Date parsing failed, use null
          safeEndDate = 'null';
          console.warn('Date parsing failed for EndDate, using null:', EndDate);
        }
      } else if (!values.delete && EndDate && typeof EndDate !== 'string') {
        // Handle React elements or other non-string types
        safeEndDate = 'null';
        console.warn('Non-string EndDate detected, using null:', typeof EndDate);
      }

      editSongEndDateAndNotes(
        playlistId ? playlistId : values.playlist,
        playlistId ? playlistId : values.playlist,
        values.songUri,
        loginState.token,
        loginState.refreshToken,
        loginState.userId,
        values.position ? values.position + 1 : setIndexes + values.index + 1,
        values.added_at,
        safeEndDate,
        'past',
        values.playId,
        values.indexes,
        values.notes,
        values.currentposition || setIndexes + values.index
      )
        .then(response => {
          toast.success(values.delete ? 'End time has been deleted' : 'End time added to playlist');
          if (values.delete) {
            setLoading2(false);
          } else {
            setLoading(false);
          }
          setIsModalVisible(false);
          // Update parent state instead of reload
          onEndDateSuccess?.(values.songUri, safeEndDate, values._id);
        })
        .catch(err => {
          toast.error('Error while adding End time!');
          if (values.delete) {
            setLoading2(false);
          } else {
            setLoading(false);
          }
        });
    }
  };

  /**
   * This function is filtering the playlists for dropdown based on owner
   * because we want the dropdown to show only those playlists scheduled by
   * songplace
   *
   * @returns {playlist[]} filtered playlists
   */
  const getPlaylistItems = playlists => {
    const playlist = [];
    playlists.forEach(data => {
      const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser'));
      if (data?.owner?.display_name === spotifyUser?.username) {
        playlist.push({
          key: data.id,
          value: data.name,
        });
      }
    });

    return playlist;
  };
  useEffect(() => {
    setData({
      ...data,
      position: ViewDetailData?.position + 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ViewDetailData?.position, currentPage]);
  useEffect(() => {
    // Reset notes when ViewDetailData changes (when switching between songs)
    if (ViewDetailData?.notes && ViewDetailData.notes.trim() !== '') {
      setNotes(ViewDetailData.notes);
    } else {
      setNotes('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ViewDetailData]);

  useEffect(() => {
    // Try endTime first, then end_time as fallback
    const dateValue = ViewDetailData?.endTime || ViewDetailData?.end_time;

    if (!dateValue || dateValue === 'null' || dateValue === null || dateValue === '') {
      setEndDate('');
    } else {
      setEndDate(dateValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ViewDetailData?.endTime, ViewDetailData?.end_time]);

  useEffect(() => {
    if (totalPlaylist.length && playlistId) {
      let playlsitName = getPlaylistItems(totalPlaylist).filter(obj => playlistId === obj.key)[0];
      setSelectedPlayList([playlsitName?.value]);

      setData({
        ...data,
        playlistId: typeof playlistId === 'string' ? [playlistId] : playlistId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPlaylist]);

  const getPlaylists = offset => {
    axios(`https://api.spotify.com/v1/me/playlists?limit=50&offset=${offset}`, {
      method: 'GET',
      headers: { Authorization: 'Bearer ' + loginState.token },
    })
      .then(genreResponse => {
        setTotalPlaylist([...totalPlaylist, ...genreResponse?.data.items]);
        if (totalPlaylistNumber < genreResponse?.data.total) {
          setTotalPlaylistNumber(genreResponse?.data.total);
        }
      })
      .catch(async error => {
        if (error.response?.data?.error?.status === 401 || error.response?.status === 400) {
          getRefreshedToken(state.refreshToken)
            .then(response => {
              addItemToStorage(SPOTIFY_TOKEN, response?.data?.access_token);

              loginDispatch({
                type: 'ADD_TOKEN',
                payload: response?.data?.access_token,
              });
            })
            .catch(() => {});
        }
      });
  };
  useEffect(() => {
    if (
      isModalVisible &&
      (totalPlaylist.length < totalPlaylistNumber || totalPlaylist.length === 0)
    ) {
      getPlaylists(totalPlaylist.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalVisible, totalPlaylistNumber, totalPlaylist]);

  return (
    <AddToPlayListModalStyle
      visible={isModalVisible}
      onOk={handleOk}
      footer={null}
      onCancel={handleCancel}
      className={`${editType}`}
    >
      {editType === 'editDate' ? (
        <>
          <div className="addToPlaylistHead" style={{ border: 'none', padding: 0 }}>
            <h1 className="heading">Edit time</h1>
          </div>
          <label className="label fullWidth editEndDate">
            <p>End time</p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
              {ADD_TO_PLAYLIST_DATE_QUICK_OFFSETS.map(({ label, unit, value }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    const endDate = moment()
                      .add(value, unit as moment.unitOfTime.DurationConstructor)
                      .endOf('day');
                    setEndDate(endDate.toISOString());
                  }}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '8px',
                    border: '1px solid #242A34',
                    background: 'transparent',
                    color: '#999',
                    fontSize: '11px',
                    cursor: 'pointer',
                    transition: 'background 0.2s, color 0.2s',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLButtonElement).style.background = '#242A34';
                    (e.currentTarget as HTMLButtonElement).style.color = '#fff';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    (e.currentTarget as HTMLButtonElement).style.color = '#999';
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <DatePicker
              transparent
              type="datetime-local"
              name="endTime"
              style={{ width: '100% !important' }}
              {...(EndDate && EndDate !== ''
                ? { value: moment(EndDate) }
                : {
                    defaultValue: (() => {
                      // Try endTime first, then end_time as fallback
                      const dateValue = ViewDetailData?.endTime || ViewDetailData?.end_time;
                      if (
                        dateValue &&
                        dateValue !== 'null' &&
                        dateValue !== '' &&
                        dateValue !== null
                      ) {
                        try {
                          const parsed = moment(dateValue);
                          return parsed;
                        } catch (error) {
                          console.error('EditAddToPlayListModal - Error parsing date:', error);
                          return undefined;
                        }
                      }
                      return undefined;
                    })(),
                  })}
              format={'DD/MM/YYYY hh:mm A'}
              onChange={value => {
                setEndDate(value ? value.toISOString() : undefined);
              }}
              disabledDate={current => {
                let customDate = moment(currentDate).format('DD/MM/YYYY hh:mm A');
                return (
                  current &&
                  moment(customDate, 'DD/MM/YYYY hh:mm A') > moment(current, 'DD/MM/YYYY hh:mm A')
                );
              }}
            />
          </label>
          <div className="buttonDiv">
            {(() => {
              const dateValue = ViewDetailData?.endTime || ViewDetailData?.end_time;
              return !dateValue ||
                dateValue === 'null' ||
                dateValue === null ||
                dateValue === '' ? null : (
                <button
                  className="addSong"
                  style={{
                    cursor: 'pointer',
                    border: '1px solid red',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'red',
                    background: 'transparent',
                    marginRight: '10px',
                  }}
                  onClick={() => {
                    handleEndDateSubmit({
                      ...ViewDetailData,
                      delete: true,
                      currentposition: ViewDetailData.position > -1 ? ViewDetailData.position : 0,
                    });
                  }}
                >
                  {!loading2 && (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.875 3.375H10.125C10.125 3.07663 10.0065 2.79048 9.7955 2.5795C9.58452 2.36853 9.29837 2.25 9 2.25C8.70163 2.25 8.41548 2.36853 8.2045 2.5795C7.99353 2.79048 7.875 3.07663 7.875 3.375ZM6.75 3.375C6.75 2.77826 6.98705 2.20597 7.40901 1.78401C7.83097 1.36205 8.40326 1.125 9 1.125C9.59674 1.125 10.169 1.36205 10.591 1.78401C11.0129 2.20597 11.25 2.77826 11.25 3.375H15.75C15.8992 3.375 16.0423 3.43426 16.1477 3.53975C16.2532 3.64524 16.3125 3.78832 16.3125 3.9375C16.3125 4.08668 16.2532 4.22976 16.1477 4.33525C16.0423 4.44074 15.8992 4.5 15.75 4.5H15.1155L13.7599 14.4427C13.6679 15.1165 13.335 15.7342 12.8226 16.1814C12.3103 16.6286 11.6533 16.875 10.9732 16.875H7.02675C6.34671 16.875 5.6897 16.6286 5.17736 16.1814C4.66503 15.7342 4.33207 15.1165 4.24013 14.4427L2.8845 4.5H2.25C2.10082 4.5 1.95774 4.44074 1.85225 4.33525C1.74676 4.22976 1.6875 4.08668 1.6875 3.9375C1.6875 3.78832 1.74676 3.64524 1.85225 3.53975C1.95774 3.43426 2.10082 3.375 2.25 3.375H6.75ZM7.875 7.3125C7.875 7.16332 7.81574 7.02024 7.71025 6.91475C7.60476 6.80926 7.46168 6.75 7.3125 6.75C7.16332 6.75 7.02024 6.80926 6.91475 6.91475C6.80926 7.02024 6.75 7.16332 6.75 7.3125V12.9375C6.75 13.0867 6.80926 13.2298 6.91475 13.3352C7.02024 13.4407 7.16332 13.5 7.3125 13.5C7.46168 13.5 7.60476 13.4407 7.71025 13.3352C7.81574 13.2298 7.875 13.0867 7.875 12.9375V7.3125ZM10.6875 6.75C10.8367 6.75 10.9798 6.80926 11.0852 6.91475C11.1907 7.02024 11.25 7.16332 11.25 7.3125V12.9375C11.25 13.0867 11.1907 13.2298 11.0852 13.3352C10.9798 13.4407 10.8367 13.5 10.6875 13.5C10.5383 13.5 10.3952 13.4407 10.2898 13.3352C10.1843 13.2298 10.125 13.0867 10.125 12.9375V7.3125C10.125 7.16332 10.1843 7.02024 10.2898 6.91475C10.3952 6.80926 10.5383 6.75 10.6875 6.75ZM5.355 14.2909C5.41021 14.6951 5.60998 15.0656 5.91732 15.3338C6.22467 15.6021 6.6188 15.7499 7.02675 15.75H10.9732C11.3814 15.7502 11.7758 15.6025 12.0834 15.3342C12.391 15.0659 12.5909 14.6953 12.6461 14.2909L13.9804 4.5H4.01962L5.355 14.2909Z"
                        fill="red"
                      />
                    </svg>
                  )}
                  {loading2 ? <Spinner /> : 'Delete date'}
                </button>
              );
            })()}
            <button
              className="addSong"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                handleEndDateSubmit({
                  ...ViewDetailData,
                  currentposition: ViewDetailData.position > -1 ? ViewDetailData.position : 0,
                });
              }}
            >
              {loading ? (
                <Spinner />
              ) : (
                `
              Edit
              `
              )}
            </button>
          </div>
        </>
      ) : editType === 'refresh' ? (
        <>
          <div className="addToPlaylistHead" style={{ border: 'none', padding: 0 }}>
            <h1 className="heading headingCenter">
              {loadingTextHead ? loadingTextHead : 'Refreshing Playlist'}
            </h1>
          </div>
          <div className="loadingDiv">
            <div className="loadingCircle"></div>
          </div>
          <p className="refreshText">{loadingText ? loadingText : 'Refreshing your playlist'}</p>
        </>
      ) : editType === 'changePlayList' ? (
        <div
          onClick={() => {
            setSelectPlayListOpen(false);
          }}
        >
          <div className="addToPlaylistHead" style={{ border: 'none', padding: 0 }}>
            <h1 className="heading">Move song</h1>
          </div>
          <div className="playlistPosition">
            <label className="label">
              <p>Playlist</p>
              <div
                className="playlistInput"
                onClick={e => {
                  e.stopPropagation();
                  setSelectPlayListOpen(true);
                }}
              >
                <div className="playlistInputText">
                  {selectedPlayList.map((a, i) => (
                    <span>{a}</span>
                  ))}
                  <input
                    className="playlistInputTextSearch"
                    value={searchPlaylistText}
                    placeholder="Search Playlist"
                    onChange={ev => {
                      setSearchPlaylistText(ev.target.value);
                    }}
                  />
                </div>
                <div className={`optionsList ${selectPlayListOpen ? 'active' : ''}`}>
                  {getPlaylistItems(totalPlaylist).map(
                    (option: { key: string; value: string; disabled?: boolean }) => {
                      return option?.value
                        ?.toLowerCase()
                        .includes(searchPlaylistText?.toLowerCase()) ? (
                        <div
                          className="optionName"
                          id="add-songs-mode-options"
                          onClick={value => {
                            value.stopPropagation();
                            if (data.playlistId.includes(option.key)) {
                              let obj = { ...data };
                              obj.playlistId.splice(obj.playlistId.indexOf(option.key), 1);
                              // @ts-ignore
                              setData(obj);
                              let playlist = [...selectedPlayList];
                              playlist.splice(playlist.indexOf(option.value), 1);
                              setSelectedPlayList([...playlist]);
                            } else {
                              let obj = { ...data };
                              // @ts-ignore
                              obj.playlistId = [...obj.playlistId, option.key];
                              setData(obj);
                              let playlist = [...selectedPlayList];
                              playlist = [...playlist, option.value];
                              setSelectedPlayList([...playlist]);
                            }
                          }}
                        >
                          <div className="optionText">
                            <p>{option.value}</p>
                            <div
                              className={`optionMark ${
                                data.playlistId.includes(option.key) ? 'active' : ''
                              }`}
                            >
                              <svg
                                width="13"
                                height="10"
                                viewBox="0 0 13 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1.5 5.5L5 9L12 1.5"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ) : null;
                    }
                  )}
                </div>
              </div>
            </label>
            <label className="label">
              <p>Position of the song in playlist</p>
              <Input
                type="number"
                style={{ border: 'none', padding: '11.5px 10px' }}
                placeholder="Position of the song in playlist"
                min={1}
                value={data.position}
                onChange={e => {
                  let obj = { ...data };
                  obj.position = Number(e.target.value);
                  setData(obj);
                }}
              />
            </label>
          </div>
          <div className="buttonDiv">
            <button
              className="addSong"
              disabled={loading}
              onClick={() => {
                if (data.position > 0) {
                  addToPlaylistLoop({
                    ...ViewDetailData,
                    playlistId: data.playlistId,
                    position: data.position,
                    currentposition: ViewDetailData.position || 0,
                  });
                } else {
                  toast.error('Position is required');
                }
              }}
            >
              {loading ? <Spinner /> : 'Move'}
            </button>
          </div>
        </div>
      ) : editType === 'addNote' ? (
        <>
          <label className="label addNoteTextLabel fullWidth">
            <p className="editNote">{ViewDetailData?.notes ? 'Edit' : 'Add'} note</p>
            <Input.TextArea
              className="addNoteText EditNoteText"
              placeholder="Write your notes here"
              value={notes}
              onChange={(ev: any) => setNotes(ev.target.value)}
              rows={4}
            />
          </label>
          <div className="editNoteButton">
            {ViewDetailData?.notes ? (
              <button
                className="addSong"
                style={{
                  cursor: 'pointer',
                  border: '1px solid red',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'red',
                  background: 'transparent',
                  marginRight: '10px',
                }}
                onClick={() => {
                  handleNotesSubmit({
                    ...ViewDetailData,
                    delete: true,
                    currentposition: ViewDetailData.position > -1 ? ViewDetailData.position : 0,
                  });
                }}
              >
                {!loading2 && (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.875 3.375H10.125C10.125 3.07663 10.0065 2.79048 9.7955 2.5795C9.58452 2.36853 9.29837 2.25 9 2.25C8.70163 2.25 8.41548 2.36853 8.2045 2.5795C7.99353 2.79048 7.875 3.07663 7.875 3.375ZM6.75 3.375C6.75 2.77826 6.98705 2.20597 7.40901 1.78401C7.83097 1.36205 8.40326 1.125 9 1.125C9.59674 1.125 10.169 1.36205 10.591 1.78401C11.0129 2.20597 11.25 2.77826 11.25 3.375H15.75C15.8992 3.375 16.0423 3.43426 16.1477 3.53975C16.2532 3.64524 16.3125 3.78832 16.3125 3.9375C16.3125 4.08668 16.2532 4.22976 16.1477 4.33525C16.0423 4.44074 15.8992 4.5 15.75 4.5H15.1155L13.7599 14.4427C13.6679 15.1165 13.335 15.7342 12.8226 16.1814C12.3103 16.6286 11.6533 16.875 10.9732 16.875H7.02675C6.34671 16.875 5.6897 16.6286 5.17736 16.1814C4.66503 15.7342 4.33207 15.1165 4.24013 14.4427L2.8845 4.5H2.25C2.10082 4.5 1.95774 4.44074 1.85225 4.33525C1.74676 4.22976 1.6875 4.08668 1.6875 3.9375C1.6875 3.78832 1.74676 3.64524 1.85225 3.53975C1.95774 3.43426 2.10082 3.375 2.25 3.375H6.75ZM7.875 7.3125C7.875 7.16332 7.81574 7.02024 7.71025 6.91475C7.60476 6.80926 7.46168 6.75 7.3125 6.75C7.16332 6.75 7.02024 6.80926 6.91475 6.91475C6.80926 7.02024 6.75 7.16332 6.75 7.3125V12.9375C6.75 13.0867 6.80926 13.2298 6.91475 13.3352C7.02024 13.4407 7.16332 13.5 7.3125 13.5C7.46168 13.5 7.60476 13.4407 7.71025 13.3352C7.81574 13.2298 7.875 13.0867 7.875 12.9375V7.3125ZM10.6875 6.75C10.8367 6.75 10.9798 6.80926 11.0852 6.91475C11.1907 7.02024 11.25 7.16332 11.25 7.3125V12.9375C11.25 13.0867 11.1907 13.2298 11.0852 13.3352C10.9798 13.4407 10.8367 13.5 10.6875 13.5C10.5383 13.5 10.3952 13.4407 10.2898 13.3352C10.1843 13.2298 10.125 13.0867 10.125 12.9375V7.3125C10.125 7.16332 10.1843 7.02024 10.2898 6.91475C10.3952 6.80926 10.5383 6.75 10.6875 6.75ZM5.355 14.2909C5.41021 14.6951 5.60998 15.0656 5.91732 15.3338C6.22467 15.6021 6.6188 15.7499 7.02675 15.75H10.9732C11.3814 15.7502 11.7758 15.6025 12.0834 15.3342C12.391 15.0659 12.5909 14.6953 12.6461 14.2909L13.9804 4.5H4.01962L5.355 14.2909Z"
                      fill="red"
                    />
                  </svg>
                )}
                {loading2 ? <Spinner /> : 'Delete notes'}
              </button>
            ) : null}
            <button
              className="addSong"
              onClick={() => {
                handleNotesSubmit({
                  ...ViewDetailData,
                  currentposition: ViewDetailData.position > -1 ? ViewDetailData.position : 0,
                });
              }}
            >
              {loading ? <Spinner /> : `${ViewDetailData?.notes ? 'Edit' : 'Add'}`}
            </button>
          </div>
        </>
      ) : editType === 'viewDetail' ? (
        <>
          <div className="viewDetailInner">
            <h1 className="heading">Song details</h1>
            <div className="albumDiv">
              <img
                alt={ViewDetailData?.track?.name}
                className="albumPic"
                src={ViewDetailData?.track?.album.images[0]?.url}
              />
              <div className="albumDetail">
                <h3>{ViewDetailData?.track?.name}</h3>
                <p>{ViewDetailData?.track?.artists[0]?.name}</p>
              </div>
            </div>
            <div className="detailBoxes1">
              <div className="box">
                <p>Song artist</p>
                <h4>{ViewDetailData?.track?.artists[0]?.name}</h4>
              </div>
              <div className="box">
                <p>Song popularity</p>
                <h4>{ViewDetailData?.track?.popularity ?? '--'}</h4>
              </div>
              <div className="box">
                <p>Playlist</p>
                <Popover content={<p>{currentPlaylistData?.name}</p>} placement="topLeft">
                  <h4 className="playlistNameViewDetail">{currentPlaylistData?.name}</h4>
                </Popover>
              </div>
            </div>
            <div className="detailBoxes2">
              <div className="box">
                <p>Original start date</p>
                <h4>
                  {ViewDetailData?.originalDate
                    ? ViewDetailData?.originalDate
                    : moment(resolvedOriginalDate).format('DD/MM/YYYY hh:mm A')}
                </h4>
              </div>
              <div className="box">
                <p>End date</p>
                <h4>
                  {(() => {
                    const dateValue = ViewDetailData?.endTime || ViewDetailData?.end_time;
                    if (
                      dateValue &&
                      dateValue !== 'null' &&
                      dateValue !== null &&
                      dateValue !== ''
                    ) {
                      return moment(dateValue).format('DD/MM/YYYY hh:mm A') !== 'Invalid date'
                        ? moment(dateValue).format('DD/MM/YYYY hh:mm A')
                        : '-';
                    }
                    return '-';
                  })()}
                </h4>
              </div>
              {ViewDetailData.lockPosition ? (
                <div className="box">
                  <p>Reset position</p>
                  <h4>{Number(ViewDetailData.lockPositionNumber) + 1}</h4>
                </div>
              ) : null}
            </div>
            <div className="detailBoxes2">
              <div className="box">
                <p>
                  Last refresh
                </p>
                <h4>
                  {PlaylistRefreshRecord.length
                    ? moment(PlaylistRefreshRecord[0].date).format('DD/MM/YYYY hh:mm A')
                    : '-'}
                </h4>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </AddToPlayListModalStyle>
  );
};

export default EditAddToPlayListModal;
